/* eslint-disable no-unused-vars, no-useless-return, no-empty-pattern, no-shadow, import/named */
/* global require, angular, document, $, Node */

/*
 * Froala WYSIWYG Editor
 * https://www.froala.com/wysiwyg-editor
 * NOTE:
 *  - Requires jQuery
 *  - Plugins are imported in /config/pluginsEnabled.js
 */

/*
 * Froala Editor w/o plugins (import each plugin as needed)
 */
import 'froala-editor/js/froala_editor.min';
import './plugins';

/*
 * Froala Editor with all plugins (for development, avoid using in production)
 */
// import 'froala-editor/js/froala_editor.pkgd.min';


import './styles.scss';
import {
  clickHandler as tokenClickHandler,
  getTokenHtml,
  mouseDownHandler as tokenMouseDownHandler,
  mouseUpHandler as tokenMouseUpHandler,
  parsePropsFromTokenData,
} from '../token';
import config from './config';
import { RTE_TOKEN_ATTR } from '../constants';

export { config };

/**
 * Drop events from INSIDE the editor. User is moving something to a new position in the document.
 * @param {Object} editor - the Froala editor object
 * @return {function(*=): boolean} Returns an $event handler with an editor clo
 */
function createNativeDropHandler(editor) {
  return ($event) => {
    // Unwrap jQuery to get native event
    const event = $event.originalEvent;

    // Get props from drop event target
    const data = event.target.getAttribute(RTE_TOKEN_ATTR);
    let props;
    if (data) {
      props = parsePropsFromTokenData(data);
    }
    if (!props) {
      // Give up
      return false;
    }

    // Move token to new position
    const tokenHtmlFrag = getTokenHtml(props);
    const tokenInserted = insertToken(editor, $event, tokenHtmlFrag);
    if (!tokenInserted) {
      // Token was not moved
      return false;
    }
    $event.target.parentElement.removeChild($event.target);

    // Stop event propagation
    $event.preventDefault();
    $event.stopPropagation();
    return false;
  };
}

/**
 * Drop events from OUTSIDE the editor.
 * This drop event is re-thrown by Froala, so it's not the original native drop event.
 * Inserts an element at the drop position.
 * Requires the e's dataTransfer to have "text" data.
 * @param {Object} editor - the Froala editor object
 * @return {function(*=): boolean} Returns an $event handler with an editor clo
 */
function createFroalaDropHandler(editor) {
  return ($event) => {
    // Unwrap jQuery to get native event
    const event = $event.originalEvent;
    console.log('token dragend (createFroalaDropHandler)!', editor, $event);

    // Get props from drop event data
    const data = event.dataTransfer.getData('text');
    if (!data.startsWith(`${RTE_TOKEN_ATTR}=`)) {
      // Oops! The event source is not RTE_TOKEN_ATTR so ignore
      return false;
    }
    // Remove "RTE_TOKEN_ATTR=" from the data because we validated the event source
    const payload = data.split('=')[1];
    const props = parsePropsFromTokenData(payload);

    // Insert token
    const tokenHtmlFrag = getTokenHtml(props);
    insertToken(editor, $event, tokenHtmlFrag);

    // Stop event propagation
    $event.preventDefault();
    $event.stopPropagation();
    return false;
  };
}

function getPreviousSibling(marker) {
  let prevSibling = marker.previousSibling;
  const isEmptyText = prevSibling.nodeType === Node.TEXT_NODE && prevSibling.nodeValue === '';
  // Skip prev sibling if it is an empty text node and go to next prev sibling
  prevSibling = isEmptyText ? prevSibling.previousSibling : prevSibling;
  return prevSibling;
}

function createDeleteHandler(editor) {
  return ($event) => {
    if ($event.key === 'Backspace') {
      // Focus at the current position
      editor.markers.insert();
      editor.selection.restore();

      // Check the element before the cursor
      const marker = editor.selection.element();
      const prevSibling = getPreviousSibling(marker);
      let isToken = false;
      if (prevSibling && prevSibling.hasAttribute) {
        isToken = prevSibling.hasAttribute('rte-token');
      }

      editor.markers.remove();

      // Delete if it's a token
      if (isToken) {
        $(prevSibling).remove();

        // Stop event propagation
        $event.preventDefault();
        $event.stopPropagation();
        return false;
      }
      return true;
    }
    return true;
  };
}

/**
 *
 * @param {Object} editor - Froala editor object
 * @param {Object} $event - jQuery event object
 * @param {string} tokenHtmlFrag - Html for the token as it is rendered in the editor
 * @return {boolean}
 */
function insertToken(editor, $event, tokenHtmlFrag) {
  // Focus at the current position
  editor.markers.insertAtPoint($event.originalEvent);
  const $marker = editor.$el.find('.fr-marker');

  if ($marker.parent().attr('rte-token')) {
    // Token was not moved ($marker is inside token)
    return false;
  }

  if ($.FroalaEditor) {
    $marker.replaceWith($.FroalaEditor.MARKERS);
  }
  editor.selection.restore();

  // Save the current position into undo stack
  if (!editor.undo.canDo()) editor.undo.saveStep();

  // Insert HTML
  editor.html.insert(tokenHtmlFrag, false);

  // Save the changes into undo stack
  editor.undo.saveStep();

  return true;
}

/**
 *
 * @param {Object|null} options - Any overrides to the Froala default configuration
 * (hint: usually this will include the tokenAttrNames property)
 * @return {Object} customConfig
 */
function mergeOptionsIntoConfig(options) {
  const htmlAllowedAttrs = [
    ...config.htmlAllowedAttrs,
    ...options.tokenAttrNames,
  ];
  const customConfig = {
    ...config,
    ...options,
    htmlAllowedAttrs,
  };
  return customConfig;
}

function getConfig(options) {
  if (options) {
    return mergeOptionsIntoConfig(options);
  }
  return config;
}

/**
 *
 * @param {jQuery} $el - The jQuery element to turn into an editor
 * @param {Object} configOptions - Overrides to the Froala Editor config.
 * See Froala Editor documentation for available options.
 * @return {jQuery} $editor Returns the jQuery editor element
 */
export const initEditor = ($el, configOptions, tokensEnabled) => {
  // Support tokens if the tokenAttrNames is provided
  if (tokensEnabled && configOptions.tokenAttrNames) {
    // Editor initialize event
    $el.on('froalaEditor.initialized', (e, editor) => {
      // Add Token drop event listener
      editor.events.on('dragend', createNativeDropHandler(editor), true);
      editor.events.on('drop', createFroalaDropHandler(editor), true);

      // Add Token event listeners
      editor.events.on('click', tokenClickHandler, true);
      editor.events.on('keydown', createDeleteHandler(editor), true);
      editor.events.on('mousedown', tokenMouseDownHandler, true);
      editor.events.on('mouseup', tokenMouseUpHandler, true);
    });
  }

  const customConfig = getConfig(configOptions);
  // FroalaEditor has a jQuery dependency
  // Create the editor the jQuery way
  return $el.froalaEditor(customConfig);
};
